import { isBefore, isWeekend } from 'date-fns'
import { sortBy } from 'lodash'
import { IBadge, IStatusDot } from '@ads/front-ds'
import {
  CreationType,
  GenericCampaignPaymentStatus,
  GenericCampaignType,
  ProductType,
  ResponseCampaignForList,
  ResponseCampaignListByFeedId,
  ResponseGenericCampaign,
  ResponseSalesCampaignOrderListItem,
} from '@ads/front-core/api'
import {
  Campaign,
  CampaignTypeItem,
  GenericCampaignTypeItem,
  MACampaignTypeItem,
  AllProductTypes,
  AllCampaignTypeItem,
  CampaignStatus,
} from '@ads/front-core/interfaces'
import { lng } from '@ads/front-core/locales'
import { parseDate } from './formatDate'
import { formatNumber } from './formatNumber'

export const campaignTypeLabels: CampaignTypeItem[] = [
  {
    id: 'RETARGETING',
    type: 'ROAS',
    label: lng('campaign:wpBooster'),
    filters: { types: ['RETARGETING'] },
  },
  {
    id: 'DISPLAY_CPSC',
    type: 'CPSC',
    label: lng('campaign:wpBooster'),
    filters: {
      types: ['DISPLAY_CPSC'],
    },
  },
  {
    id: 'PROSPECTING_2',
    type: 'CPC',
    label: lng('campaign:dynamicDisplay'),
    filters: { types: ['PROSPECTING', 'PROSPECTING_2'] },
  },
  {
    id: 'FEED_CPSC',
    type: 'CPSC',
    label: lng('campaign:dynamicCSPS.shortTitle'),
    filters: { types: ['FEED_CPSC'] },
  },
  {
    id: 'DISPLAY_CPC_BANNER_HTML',
    type: 'CPC',
    label: lng('campaign:display'),
    filters: {
      types: ['DISPLAY_CPC', 'UNIVERSAL_CPC'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE', 'RICH_MEDIA'],
    },
  },
  {
    id: 'DISPLAY_CPM',
    type: 'CPM',
    label: lng('campaign:display'),
    filters: {
      types: ['DISPLAY_CPM', 'UNIVERSAL_CPM'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE', 'RICH_MEDIA'],
    },
  },
  {
    id: 'DISPLAY_VCPM',
    type: 'vCPM',
    label: lng('campaign:display'),
    filters: {
      types: ['DISPLAY_VCPM', 'UNIVERSAL_VCPM'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE', 'RICH_MEDIA'],
    },
  },
  {
    id: 'DISPLAY_CPC_NATIVE',
    type: 'CPC',
    label: lng('campaign:nativeAdvertising'),
    filters: {
      types: ['DISPLAY_CPC'],
      allowedCreationTypes: ['NATIVE'],
    },
  },
  {
    id: 'PUBLISHERS_DISPLAY_CPC',
    type: 'CPC',
    label: lng('campaign:displayCPCPublishers'),
    filters: {
      types: ['PUBLISHERS_DISPLAY_CPC'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE'],
    },
  },
  {
    id: 'MAILING_PROSPECTING',
    type: 'CPC',
    label: lng('campaign:mailingProspecting'),
    filters: {
      types: ['MAILING_PROSPECTING'],
    },
  },
  {
    id: 'MAILING_RETARGETING',
    type: 'CPC',
    label: lng('campaign:mailingRetargeting.mailingRetargeting'),
    filters: { types: ['MAILING_RETARGETING'] },
  },
  {
    id: 'MAILING_STATIC',
    type: 'CPC',
    label: lng('campaign:mailingStatic.mailingShortNameInbox'),
    filters: {
      types: ['MAILING_STATIC'],
    },
  },
  {
    id: 'MAILING_STATIC_OTHER',
    type: 'CPC',
    label: lng('campaign:mailingStatic.mailingShortNameOther'),
    filters: {
      types: ['MAILING_STATIC_OTHER'],
    },
  },
  {
    id: 'MAILING_STATIC_CPM',
    type: 'CPM',
    label: lng('campaign:mailingStaticCPM.mailingShortName'),
    filters: {
      types: ['MAILING_STATIC_CPM'],
    },
  },
  {
    id: 'NATIVE_LINK_DIRECT',
    type: 'CPC',
    label: lng('campaign:nativeLink.title'),
    filters: {
      types: ['NATIVE_LINK_DIRECT'],
    },
  },
  {
    id: 'NATIVE_SG',
    type: undefined,
    label: lng('campaign:nativeSG.title'),
    filters: {
      types: ['NATIVE_SG'],
    },
  },
  {
    id: 'PUBLISHERS_DISPLAY_VCPM',
    type: 'vCPM',
    label: lng('campaign:displayVCPMPublishers'),
    filters: {
      types: ['PUBLISHERS_DISPLAY_VCPM'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE'],
    },
  },
  {
    id: 'PUBLISHERS_DISPLAY_CPM',
    type: 'CPM',
    label: lng('campaign:displayCPMPublishers'),
    filters: {
      types: ['PUBLISHERS_DISPLAY_CPM'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE'],
    },
  },
  {
    id: 'PUBLISHERS_FLAT_FEE',
    type: undefined,
    label: lng('campaign:flatFeePublishers'),
    filters: {
      types: ['PUBLISHERS_FLAT_FEE'],
      allowedCreationTypes: ['BANNER', 'HTML', 'CODE'],
    },
  },
  {
    id: 'PUBLISHERS_SCREENING',
    type: 'CPC',
    label: lng('campaign:screeningPublisher.screeningPublishers'),
    filters: {
      types: ['PUBLISHERS_SCREENING'],
      allowedCreationTypes: ['SCREENING_ROS'],
    },
  },
  {
    id: 'START_AD',
    type: 'CPM',
    label: lng('campaign:startPauseAd.startAd.title'),
    filters: {
      types: ['START_AD'],
    },
  },
  {
    id: 'PAUSE_AD',
    type: 'CPM',
    label: lng('campaign:startPauseAd.pauseAd.title'),
    filters: {
      types: ['PAUSE_AD'],
    },
  },
  {
    id: 'OUTSTREAM_VIDEO_CPM',
    type: 'CPM',
    label: lng('campaign:outstreamInBanner.title'),
    filters: {
      types: ['OUTSTREAM_VIDEO_CPM'],
    },
  },
  {
    id: 'OUTSTREAM_VIDEO_VCPM',
    type: 'vCPM',
    label: lng('campaign:outstreamInBanner.title'),
    filters: {
      types: ['OUTSTREAM_VIDEO_VCPM'],
    },
  },
]

export const maCampaignTypeLabels: MACampaignTypeItem[] = [
  {
    id: 'PUSH',
    type: null,
    label: lng('campaign:push.title'),
    filters: {
      types: ['PUSH'],
    },
  },
  {
    id: 'NEWSLETTER',
    type: null,
    label: lng('newsletters:title'),
    filters: {
      types: ['NEWSLETTER'],
    },
  },
]

const genericCampaignTypeLabelsNoSorted: GenericCampaignTypeItem[] = [
  {
    id: 'ARTICLE_NATIVE',
    type: undefined,
    label: lng('campaign:marketplace.articles.titleNative'),
    filters: {
      types: ['ARTICLE_NATIVE'],
    },
  },
  {
    id: 'ARTICLE_SPONSORED',
    type: undefined,
    label: lng('campaign:marketplace.articles.titleSponsored'),
    filters: {
      types: ['ARTICLE_SPONSORED'],
    },
  },
  {
    id: 'ARTICLE_SPONSORED_GUARANTEED',
    type: undefined,
    label: lng('campaign:marketplace.articles.titleSponsoredGuaranteed'),
    filters: {
      types: ['ARTICLE_SPONSORED_GUARANTEED'],
    },
  },
  {
    id: 'BAR',
    type: undefined,
    label: lng('campaign:marketplace.barAd'),
    filters: {
      types: ['BAR'],
    },
  },
  {
    id: 'BAR_LP_WP',
    type: undefined,
    label: lng('campaign:marketplace.barLPWPAd'),
    filters: {
      types: ['BAR_LP_WP'],
    },
  },
  {
    id: 'BUSINESS_SERVICES',
    type: undefined,
    label: lng('campaign:marketplace.businessAd'),
    filters: {
      types: ['BUSINESS_SERVICES'],
    },
  },
  {
    id: 'BROAD_FLEX',
    type: undefined,
    label: lng('campaign:marketplace.broadFlex.title'),
    filters: {
      types: ['BROAD_FLEX'],
    },
  },
  {
    id: 'COMMERCIAL_BREAK',
    type: undefined,
    label: lng('campaign:marketplace.commercialBreak.title'),
    filters: {
      types: ['COMMERCIAL_BREAK'],
    },
  },
  {
    id: 'CONTENT_BOX',
    type: undefined,
    label: lng('campaign:marketplace.contentBoxAd'),
    filters: {
      types: ['CONTENT_BOX'],
    },
  },
  {
    id: 'FLAT_FEE',
    type: undefined,
    label: lng('campaign:flatFee'),
    filters: {
      types: ['FLAT_FEE'],
    },
  },
  {
    id: 'FLOATING_HALFPAGE',
    type: undefined,
    label: lng('campaign:marketplace.floatingHalfPage'),
    filters: {
      types: ['FLOATING_HALFPAGE'],
    },
  },
  {
    id: 'GIGABOARD',
    type: undefined,
    label: lng('campaign:marketplace.gigaboard.title'),
    filters: {
      types: ['GIGABOARD'],
    },
  },
  {
    id: 'MIX',
    type: undefined,
    label: lng('campaign:marketplace.mix.title'),
    filters: {
      types: ['MIX'],
    },
  },
  {
    id: 'PERFORMANCE_ORDER',
    type: undefined,
    label: lng('campaign:marketplace.performanceOrder.title'),
    filters: {
      types: ['PERFORMANCE_ORDER'],
    },
  },
  {
    id: 'SPORTS_SERVICES',
    type: undefined,
    label: lng('campaign:marketplace.sportsAd'),
    filters: {
      types: ['SPORTS_SERVICES'],
    },
  },
  {
    id: 'SCREENING',
    type: undefined,
    label: lng('campaign:marketplace.screening.title'),
    filters: {
      types: ['SCREENING'],
    },
  },
  {
    id: 'STICKY',
    type: undefined,
    label: lng('campaign:marketplace.stickyMobileAd'),
    filters: {
      types: ['STICKY'],
    },
  },
  {
    id: 'WEB_PORTALS',
    type: undefined,
    label: lng('campaign:marketplace.webPortalsAd'),
    filters: {
      types: ['WEB_PORTALS'],
    },
  },
  {
    id: 'WP_FLEX',
    type: undefined,
    label: lng('campaign:marketplace.wpFlex.title'),
    filters: {
      types: ['WP_FLEX'],
    },
  },
  {
    id: 'VIDEO',
    type: undefined,
    label: 'Video',
    filters: {
      types: ['VIDEO'],
    },
  },
]

export const genericCampaignTypeLabels = sortBy(
  genericCampaignTypeLabelsNoSorted,
  'label',
)

export const allNonMACampaignTypeLabels = [
  ...campaignTypeLabels,
  ...genericCampaignTypeLabels,
]

export const allCampaignTypeLabels = [
  ...campaignTypeLabels,
  ...genericCampaignTypeLabels,
  ...maCampaignTypeLabels,
]

export const isGenericCampaign = (productType: AllProductTypes): boolean =>
  genericCampaignTypeLabels.some(g => g.filters.types.includes(productType))

export const isArticle = (type: AllProductTypes) =>
  type === 'ARTICLE_NATIVE' ||
  type === 'ARTICLE_SPONSORED' ||
  type === 'ARTICLE_SPONSORED_GUARANTEED'

export const isFlatFee = (type: AllProductTypes) => type === 'FLAT_FEE'

export const getCampaignTypeLabelById = (
  id: string,
): AllCampaignTypeItem | undefined =>
  allCampaignTypeLabels.find(c => c.id === id)

export const getCampaignTypeLabel = (filters: {
  type?: AllProductTypes
  allowedCreationTypes?: Array<CreationType>
}): AllCampaignTypeItem | undefined => {
  const filteredLabels = allCampaignTypeLabels.filter(c => {
    if (filters.type && !c.filters.types.includes(filters.type)) {
      return false
    }
    if (
      filters.allowedCreationTypes?.length > 0 &&
      c.filters?.['allowedCreationTypes']?.length > 0 &&
      !c.filters?.['allowedCreationTypes']?.some(act =>
        filters.allowedCreationTypes.includes(act),
      )
    ) {
      return false
    }
    return true
  })
  return filteredLabels?.[0]
}

export const getCampaignTypeLabelType = (
  type: AllProductTypes,
): AllCampaignTypeItem =>
  allCampaignTypeLabels.find(campaign => campaign.filters.types.includes(type))

export const campaignStatusLabels: Record<
  Exclude<CampaignStatus, 'WAIT_FOR_CONFIRMATION' | 'WAIT_FOR_START_DATE'>,
  { label: string }
> = {
  DRAFT: {
    label: lng('campaign:draft'),
  },
  RESERVATION: {
    label: lng('flatfee:reservation'),
  },
  INCOMPLETE: {
    label: lng('campaign:incomplete'),
  },
  WAITING: {
    label: lng('campaign:waitForConfirmation'),
  },
  STARTED: {
    label: lng('campaign:started'),
  },
  PAUSED: {
    label: lng('campaign:paused'),
  },
  ENDED: {
    label: lng('campaign:ended'),
  },
  IN_VERIFICATION: {
    label: lng('campaign:inVerification'),
  },
  REJECTED: {
    label: lng('common:rejected'),
  },
}

export const getCampaignStatusLabel = (
  campaignStatus: CampaignStatus,
  userPaused?: boolean,
): { label: string } | undefined =>
  campaignStatus === 'PAUSED' && userPaused
    ? { label: lng('campaign:pausedByUser') }
    : campaignStatus === 'WAIT_FOR_CONFIRMATION' ||
      campaignStatus === 'WAIT_FOR_START_DATE'
    ? { label: lng('campaign:waitForConfirmation') }
    : campaignStatusLabels[campaignStatus]

export const getCampaignStatus = (
  campaign?:
    | ResponseCampaignForList
    | Campaign
    | ResponseGenericCampaign
    | ResponseCampaignListByFeedId['list'][number]
    | ResponseSalesCampaignOrderListItem,
): {
  label: string
  badgeVariant: IBadge['variant']
  badgeAppearance: IBadge['appearance']
  statusDotVariant: IStatusDot['variant']
} => {
  if (!campaign) {
    return {
      statusDotVariant: 'grey',
      badgeVariant: 'default',
      badgeAppearance: 'subtle',
      label: lng('campaign:draft'),
    }
  }

  try {
    if (
      campaign?.type === 'RETARGETING' &&
      ['STARTED', 'PAUSED'].includes(String(campaign?.status)) &&
      isBefore(parseDate(campaign?.dateEnd), new Date())
    ) {
      campaign.status = 'PAUSED'
    }
  } catch (e) {
    console.error(e)
  }

  switch (campaign?.status) {
    default:
      return {
        statusDotVariant: 'grey',
        badgeVariant: 'default',
        badgeAppearance: 'subtle',
        label: lng('common:unknown'),
      }
    case 'DRAFT':
      return {
        statusDotVariant: 'grey',
        badgeVariant: 'default',
        badgeAppearance: 'subtle',
        label: lng(
          campaign?.type === 'FLAT_FEE'
            ? 'flatfee:reservation'
            : 'campaign:draft',
        ),
      }
    case 'INCOMPLETE':
      return {
        statusDotVariant: 'red',
        badgeVariant: 'alert',
        badgeAppearance: 'solid',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
    case 'PAUSED':
      return {
        statusDotVariant: 'orange',
        badgeVariant: 'warning',
        badgeAppearance: 'solid',
        label: getCampaignStatusLabel(
          campaign?.status,
          (campaign as Campaign)?.userPaused,
        )?.label,
      }
    case 'WAIT_FOR_CONFIRMATION':
      return {
        statusDotVariant: 'grey',
        badgeVariant: 'neutral',
        badgeAppearance: 'solid',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
    case 'STARTED':
      return {
        statusDotVariant: 'green',
        badgeVariant: 'success',
        badgeAppearance: 'subtle',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
    case 'ENDED':
      return {
        statusDotVariant: 'blue',
        badgeVariant: 'neutral',
        badgeAppearance: 'subtle',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
    case 'IN_VERIFICATION':
      return {
        statusDotVariant: 'grey',
        badgeVariant: 'neutral',
        badgeAppearance: 'subtle',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
    case 'REJECTED':
      return {
        statusDotVariant: 'red',
        badgeVariant: 'alert',
        badgeAppearance: 'subtle',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
    case 'WAIT_FOR_START_DATE':
      return {
        statusDotVariant: 'green',
        badgeVariant: 'success',
        badgeAppearance: 'subtle',
        label: getCampaignStatusLabel(campaign?.status)?.label,
      }
  }
}

export const getGenericCampaignPaymentStatus = (
  paymentStatus: GenericCampaignPaymentStatus,
): {
  statusDotVariant: IStatusDot['variant']
  label: string
} => {
  switch (paymentStatus) {
    case 'PAID':
      return {
        statusDotVariant: 'green',
        label: lng('campaign:paid'),
      }
    case 'UNPAID':
      return {
        statusDotVariant: 'red',
        label: lng('campaign:unpaid'),
      }
    case 'PENDING':
      return {
        statusDotVariant: 'yellow',
        label: lng('campaign:pending'),
      }
  }
}

export const isPublisherCampaign = (productType: AllProductTypes) =>
  [
    'PUBLISHERS_DISPLAY_VCPM',
    'PUBLISHERS_DISPLAY_CPM',
    'PUBLISHERS_DISPLAY_CPC',
    'PUBLISHERS_FLAT_FEE',
    'PUBLISHERS_SCREENING',
  ].includes(productType)

export const isCampaignWithSlots = (productType: AllProductTypes) =>
  [
    'PUBLISHERS_DISPLAY_VCPM',
    'PUBLISHERS_DISPLAY_CPM',
    'PUBLISHERS_FLAT_FEE',
  ].includes(productType)

export const getProductTypesByMetricTypes = (
  selectedCampaignTypeIds: string[],
): ProductType[] | GenericCampaignType[] => {
  const productTypes: ProductType[] = allCampaignTypeLabels.reduce(
    (acc, campaignTypeLabel) => {
      if (selectedCampaignTypeIds.includes(campaignTypeLabel.id)) {
        if (campaignTypeLabel.id === 'DISPLAY_CPC_NATIVE') {
          return [...acc, 'DISPLAY_CPC_NATIVE']
        } else if (campaignTypeLabel.filters.types?.length > 0) {
          return [...acc, ...campaignTypeLabel.filters.types]
        }
      }
      return acc
    },
    [],
  )
  return productTypes
}

const FLAT_FEE_DISCOUNT = 0.8 // 80% base price

export const getFlatFeeStatsMinMax = (
  value: number,
  date: string | Date,
): { min: string; max: string } => {
  if (isWeekend(parseDate(date))) {
    return {
      min: formatFlatFeeStats(value * FLAT_FEE_DISCOUNT * FLAT_FEE_DISCOUNT),
      max: formatFlatFeeStats(value * FLAT_FEE_DISCOUNT),
    }
  }
  return {
    min: formatFlatFeeStats(value * FLAT_FEE_DISCOUNT),
    max: formatFlatFeeStats(value),
  }
}

export const hasFlatFeeEstimatedPV = (productName: string): boolean => {
  const products = ['WP Power Get 3', 'WP Power Get 5', 'Power GO']
  return !new RegExp(products.join('|'), 'i').test(productName)
}

export const formatFlatFeeStats = (value: number): string => {
  const res = formatNumber(value, {
    fractionDigits: 1,
    orderOfMagnitude: 'auto',
  })

  const resMatch = res.match(/^(\d+),(\d+)/)
  if (!resMatch) {
    return res
  }
  if (resMatch[1].length > 1) {
    return formatNumber(value, {
      fractionDigits: 0,
      orderOfMagnitude: 'auto',
    })
  }

  return res
}
